import React, { useState, useEffect, useCallback, useRef } from "react";
import {
  TeamInformation,
  TeamStatsTable,
  ComparisonStatTable,
  PlayerStatsTable,
  PlayerStatsResume,
} from "../components";
import { calculatePercj, calculatePlayerStats } from "../lib/cabbStats";
import {
  Chart as ChartJS,
  RadialLinearScale,
  PointElement,
  LineElement,
  Filler,
  Tooltip,
  Legend,
  Title,
  CategoryScale,
  LinearScale,
  BarElement,
} from "chart.js";
import { Radar, Bar } from "react-chartjs-2";

ChartJS.register(
  RadialLinearScale,
  PointElement,
  LineElement,
  Filler,
  Tooltip,
  Legend,
  Title,
  CategoryScale,
  LinearScale,
  BarElement
);
export const optionsBarChart = {
  responsive: true,
  plugins: {
    legend: {
      position: "top",
    },
    title: {
      display: false,
    },
  },
};

const calculateMetric = (games, team, metric) => {
  return calculatePercj(games, team, metric)?.toFixed(1);
};

const TitleTool = ({ data }) => {
  return (
    <>
      <p
        contentEditable
        suppressContentEditableWarning
        data-placeholder="Escribe aquí tu título"
        style={{ fontSize: "30px", fontWeight: "700", outline: "none" }}
      >
        {data !== "" ? data : null}
      </p>
      <style>
        {`
        [contenteditable][data-placeholder]:empty:before {
          content: attr(data-placeholder);
          color: #ccc;
          pointer-events: none;
        }
      `}
      </style>
    </>
  );
};

const LinkTool = ({ data = "", onChange }) => {
  const [showLinkDialog, setShowLinkDialog] = useState(false);
  const [linkUrl, setLinkUrl] = useState("");
  const [selection, setSelection] = useState(null);
  const titleRef = useRef(null);

  // Manejar atajos de teclado
  const handleKeyDown = (e) => {
    // Ctrl+K o Cmd+K para añadir enlace
    if ((e.ctrlKey || e.metaKey) && e.key === "k") {
      e.preventDefault();
      const selection = window.getSelection();
      if (selection.toString().length > 0) {
        setSelection(selection.getRangeAt(0).cloneRange());
        setShowLinkDialog(true);
      }
    }
  };

  // Cerrar el diálogo al presionar Escape
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === "Escape" && showLinkDialog) {
        setShowLinkDialog(false);
      }
    };

    window.addEventListener("keydown", handleEscape);
    return () => window.removeEventListener("keydown", handleEscape);
  }, [showLinkDialog]);

  const handleContentChange = (e) => {
    if (onChange) {
      onChange({
        html: e.target.innerHTML,
        url: linkUrl,
      });
    }
  };
  const handleUrlChange = (newUrl) => {
    let formattedUrl = newUrl.trim();
    if (!/^https?:\/\//i.test(formattedUrl)) {
      formattedUrl = "https://" + formattedUrl;
    }
    setLinkUrl(formattedUrl);
  };

  return (
    <div className="title-tool-container">
      <div style={{ marginBottom: "10px" }} id="link-button">
        <button
          onClick={() => setShowLinkDialog(true)}
          style={{
            padding: "5px 10px",
            background: "#f0f0f0",
            border: "1px solid #ccc",
            borderRadius: "4px",
            cursor: "pointer",
          }}
        >
          Configurar enlace
        </button>
      </div>
      {/* Diálogo para configurar el enlace */}
      {showLinkDialog && (
        <div
          style={{
            position: "fixed",
            top: "0",
            left: "0",
            right: "0",
            bottom: "0",
            backgroundColor: "rgba(0, 0, 0, 0.5)",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            zIndex: 1000,
          }}
        >
          <div
            style={{
              backgroundColor: "white",
              padding: "20px",
              borderRadius: "8px",
              width: "300px",
            }}
          >
            <h3 style={{ marginTop: 0 }}>Configurar URL del enlace</h3>

            <div style={{ marginBottom: "15px" }}>
              <label
                htmlFor="link"
                style={{ display: "block", marginBottom: "5px" }}
              >
                URL del enlace
              </label>
              <input
                id="link"
                type="text"
                value={linkUrl}
                onChange={(e) => setLinkUrl(e.target.value)}
                placeholder="https://ejemplo.com"
                style={{
                  width: "100%",
                  padding: "8px",
                  border: "1px solid #ccc",
                  borderRadius: "4px",
                }}
                autoFocus
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    handleUrlChange(linkUrl);
                    setShowLinkDialog(false);
                  } else if (e.key === "Escape") {
                    setShowLinkDialog(false);
                  }
                }}
              />
            </div>

            <div
              style={{
                display: "flex",
                justifyContent: "flex-end",
                gap: "10px",
              }}
            >
              <button
                onClick={() => setShowLinkDialog(false)}
                style={{
                  padding: "5px 10px",
                  background: "#f0f0f0",
                  border: "1px solid #ccc",
                  borderRadius: "4px",
                  cursor: "pointer",
                }}
              >
                Cancelar
              </button>
              <button
                onClick={() => {
                  handleUrlChange(linkUrl);
                  setShowLinkDialog(false);
                }}
                style={{
                  padding: "5px 10px",
                  background: "#0066cc",
                  color: "white",
                  border: "1px solid #0055aa",
                  borderRadius: "4px",
                  cursor: "pointer",
                }}
              >
                Guardar
              </button>
            </div>
          </div>
        </div>
      )}

      <div
        className="title-link-wrapper"
        onClick={(e) => {
          // Solo navegar si no se está editando el texto
          if (window.getSelection().toString() === "" && linkUrl) {
            window.open(linkUrl, "_blank", "noopener,noreferrer");
          }
        }}
        data-link={linkUrl}
        style={{
          cursor: linkUrl ? "pointer" : "text",
        }}
      >
        <div
          id="link-content"
          ref={titleRef}
          contentEditable
          suppressContentEditableWarning
          data-placeholder="Escribe aquí tu título"
          style={{
            fontSize: "30px",
            fontWeight: "700",
            outline: "none",
            minHeight: "40px",
            textDecoration: linkUrl ? "underline" : "none",
            color: linkUrl ? "#0066cc" : "inherit",
          }}
          onKeyDown={handleKeyDown}
          onInput={handleContentChange}
          dangerouslySetInnerHTML={{ __html: data }}
        />
      </div>
      <style>
        {`
      [contenteditable][data-placeholder]:empty:before {
        content: attr(data-placeholder);
        color: #ccc;
        pointer-events: none;
      }
      `}
      </style>
    </div>
  );
};

const ImageTool = ({ onImageLoad }) => {
  const [selectedImage, setSelectedImage] = useState(null);

  const handleImageChange = useCallback(
    (event) => {
      event.preventDefault();
      const file = event.target.files[0];
      if (file) {
        const imageUrl = URL.createObjectURL(file);
        setSelectedImage(imageUrl);

        const img = new Image();
        img.onload = () => {
          const newDimensions = {
            width: img.naturalWidth,
            height: img.naturalHeight,
          };
          if (typeof onImageLoad === "function") {
            onImageLoad(newDimensions.width, newDimensions.height);
          }
        };
        img.src = imageUrl;
      }
    },
    [onImageLoad]
  );

  return (
    <div>
      {selectedImage ? (
        <>
          <img
            src={selectedImage}
            alt="Selected"
            style={{
              width: "100%",
              height: "auto",
              objectFit: "contain",
              maxWidth: "100%",
              position: "relative",
            }}
          />
          <button
            className="no-print btn-primary ghost"
            id="change-image-btn"
            style={{
              width: "25px",
              height: "25px",
              top: 5,
              right: 5,
              position: "absolute",
              padding: "0.2rem",
            }}
            onClick={() => setSelectedImage(null)}
          >
            X
          </button>
        </>
      ) : (
        <input
          type="file"
          id="avatar"
          name="avatar"
          accept="image/png, image/jpeg"
          onChange={handleImageChange}
        />
      )}
    </div>
  );
};

const FeaturedTable = ({
  activeSeason,
  activePhase,
  baseTeam,
  data,
  games,
  players,
}) => {
  const [selectedValue, setSelectedValue] = useState(data);

  return (
    <table>
      <thead>
        <tr
          className="header"
          style={{
            background: "#A5A5A5",
            fontWeight: "700",
            color: "#FFF",
            fontSize: "20px",
          }}
        >
          <td style={{ padding: "1rem" }}>
            <select
              value={selectedValue}
              onChange={(e) => setSelectedValue(e.target.value)}
              style={{
                background: "transparent",
                border: "none",
                fontWeight: "700",
                color: "#FFF",
              }}
            >
              <option value="eFG">eFG</option>
              <option value="USG">usg</option>
              <option value="TWOOPP">2pts</option>
              <option value="THREEPP">3pts</option>
              <option value="FTP">TL mejores</option>
              <option value="FTPW">TL peores</option>
            </select>
          </td>
          <td>Promedio</td>
        </tr>
      </thead>
      {games === null || data === null || games === undefined ? (
        <tbody>
          {[...Array(5)].map((_, i) => (
            <tr key={i}>
              <td>Nombre</td>
              <td className="value">0</td>
            </tr>
          ))}
        </tbody>
      ) : (
        <tbody>
          {players
            ?.map((player) => ({
              ...player,
              statValue: calculatePlayerStats(
                games.filter(
                  (game) =>
                    (game.local_team === baseTeam ||
                      game.visit_team === baseTeam) &&
                    game.season === activeSeason &&
                    (activePhase === null || game.stage === activePhase)
                ),
                player._id,
                selectedValue === "FTPW" ? "FTP" : selectedValue
              ),
            }))
            .filter(
              (player) =>
                player.statValue > 0 &&
                calculatePlayerStats(
                  games.filter(
                    (game) =>
                      (game.local_team === baseTeam ||
                        game.visit_team === baseTeam) &&
                      game.season === activeSeason &&
                      (activePhase === null || game.stage === activePhase)
                  ),
                  player._id,
                  "MINSP"
                ) > 5
            )
            .sort((a, b) => {
              if (selectedValue === "FTPW") {
                return a.statValue - b.statValue;
              } else {
                return b.statValue - a.statValue;
              }
            })
            .slice(0, 5)
            .map((player, index) => (
              <tr key={index}>
                <td>
                  {player.player_name.toUpperCase()}{" "}
                  {player.player_lastname.toUpperCase()}
                </td>
                <td className="value">{player.statValue}</td>
              </tr>
            ))}
        </tbody>
      )}
    </table>
  );
};

const Table = ({ rows, cols, data }) => {
  const [tableData, setTableData] = useState(
    data || Array.from({ length: rows }, () => Array(cols).fill(""))
  );

  useEffect(() => {
    if (data === null || data === undefined) {
      setTableData(
        Array.from({ length: rows }, (_, rowIndex) =>
          Array.from(
            { length: cols },
            (_, colIndex) => tableData[rowIndex]?.[colIndex] || ""
          )
        )
      );
    } else {
      setTableData(
        Array.from({ length: data.length }, (_, rowIndex) =>
          Array.from(
            { length: data[0].length },
            (_, colIndex) => tableData[rowIndex]?.[colIndex] || ""
          )
        )
      );
    }
  }, [rows, cols]);

  return (
    <table style={{ borderSpacing: 0, width: "100%", height: "100%" }}>
      <tbody>
        {tableData.map((row, rowIndex) => (
          <tr
            key={rowIndex}
            className={rowIndex === 0 ? "header" : null}
            style={
              rowIndex === 0
                ? { background: "#A5A5A5", fontWeight: "700", color: "#FFF" }
                : null
            }
          >
            {row.map((cell, colIndex) => (
              <td
                key={colIndex}
                contentEditable
                suppressContentEditableWarning
                style={{
                  width: `${100 / row.length}%`,
                  height: `${100 / cell.length}%`,
                  border: "1px solid rgb(151,152,154)",
                  gap: 0,
                }}
              >
                {cell}
              </td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  );
};

const List = ({ items, listType }) => {
  const [isList, setIsList] = useState(null);
  return (
    <>
      {isList ? (
        <ul
          className="listTool"
          contentEditable
          suppressContentEditableWarning
          data-placeholder="Escribe aquí tu título"
          style={{
            outline: "none",
          }}
        >
          {items && items.length > 0 ? (
            items.map((item, index) => <li key={index}>{item}</li>)
          ) : (
            <li>Nuevo ítem</li>
          )}
        </ul>
      ) : listType === "BulletList" ? (
        <ul className="listTool" contentEditable suppressContentEditableWarning>
          {items && items.length > 0 ? (
            items.map((item, index) => <li key={index}>{item}</li>)
          ) : (
            <li>Nuevo ítem</li>
          )}
        </ul>
      ) : listType === "NumberList" ? (
        <ol className="listTool" contentEditable suppressContentEditableWarning>
          {items && items.length > 0 ? (
            items.map((item, index) => <li key={index}>{item}</li>)
          ) : (
            <li>Nuevo ítem</li>
          )}
        </ol>
      ) : (
        <div
          className="noteTool"
          style={{
            outline: "none",
          }}
        >
          <p
            contentEditable
            suppressContentEditableWarning
            data-placeholder="Escribe aquí"
          ></p>
          <style>
            {`
        [contenteditable][data-placeholder]:empty:before {
          content: attr(data-placeholder);
          color: #ccc;
          pointer-events: none;
        }
      `}
          </style>
        </div>
      )}
    </>
  );
};

const TeamPerformance = ({ games, activeSeason, activePhase, baseTeam }) => {
  let activeTeamGames = games?.filter(
    (game) => game.local_team === baseTeam || game.visit_team === baseTeam
  );

  return (
    <>
      <TeamInformation
        teamId={baseTeam}
        games={activeTeamGames}
        activeSeason={activeSeason}
        activePhase={activePhase}
        isDNDTool={true}
      />
    </>
  );
};

const BasicTeamStats = ({ games, baseTeam, activeSeason, activePhase }) => {
  return (
    <>
      <TeamStatsTable
        games={games}
        activeProfile={baseTeam}
        activeSeason={activeSeason}
        activePhase={activePhase}
        isDNDTool={true}
      />
    </>
  );
};

const AdvancedTeamStats = ({ games, baseTeam, activeSeason, activePhase }) => {
  return (
    <>
      <TeamStatsTable
        games={games}
        activeProfile={baseTeam}
        activeSeason={activeSeason}
        activePhase={activePhase}
        isAdvancedStats={true}
        isDNDTool={true}
      />
    </>
  );
};

const ComparisonPlayerChart = ({
  games,
  baseTeam,
  activeSeason,
  activePhase,
  players,
  activeProfileId,
}) => {
  const [activeProfile, setActiveProfile] = useState(null);

  return (
    <>
      <ComparisonStatTable
        isDnD={true}
        players={players}
        activeProfile={activeProfile}
        activeProfileId={activeProfile}
        setActiveProfile={setActiveProfile}
        activeGames={games}
        activeSeason={activeSeason}
        activePhase={activePhase}
      />
    </>
  );
};

const PlayerResume = ({
  games,
  baseTeam,
  activeSeason,
  activePhase,
  players,
}) => {
  const [activeProfile, setActiveProfile] = useState(null);

  return (
    <>
      <PlayerStatsResume
        isDnD={true}
        players={players}
        activeProfile={players?.find((e) => e._id === activeProfile)}
        setActiveProfile={setActiveProfile}
        activeGames={games}
        activeSeason={activeSeason}
        activePhase={activePhase}
      />
    </>
  );
};

const PlayerStatistics = ({
  games,
  baseTeam,
  activeSeason,
  activePhase,
  players,
}) => {
  const [activeProfile, setActiveProfile] = useState(null);

  return (
    <>
      <PlayerStatsTable
        isDnD={true}
        players={players}
        activeProfileId={activeProfile}
        activeProfile={players?.find((e) => e._id === activeProfile)}
        setActiveProfile={setActiveProfile}
        activeGames={games}
        activeSeason={activeSeason}
        activePhase={activePhase}
      />
    </>
  );
};

const ComparisonBarChart = ({
  games,
  baseTeam,
  activeRival,
  activeTeam,
  data,
  activeSeason,
  activePhase,
  activeLeague,
  isH2HActive,
  teams,
}) => {
  const [chartData, setChartData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedChart, setSelectedChart] = useState("");

  // Evitar cálculos antes de tener datos
  const isDataReady =
    games &&
    Array.isArray(games) &&
    teams &&
    Array.isArray(teams) &&
    activeTeam;

  // --- labels ---
  const reboundingLabels = [
    "Rebotes ofensivos",
    "Rebotes defensivos",
    "Rebotes totales",
    "Porcentaje de rebotes ofensivos",
    "Porcentaje de rebotes defensivos",
  ];
  const shootingLabels = [
    "Porcentaje de 3 puntos",
    "Porcentaje de 2 puntos",
    "Porcentaje de 1 punto",
    "Porcentaje de tiros de campo",
    "Porcentaje real de tiro",
  ];
  const assitsLabels = [
    "Asistencias por partido",
    "Recuperos por partido",
    "Pérdidas por partido",
  ];
  const extraLabels = [
    "Faltas cometidas",
    "Faltas recibidas",
    "Tapas cometidas",
    "Tapas recibidas",
  ];

  // --- Actualizar chart seleccionado ---
  useEffect(() => {
    if (data) {
      setSelectedChart(data);
      setLoading(false);
    }
  }, [data]);

  // --- Evitar ejecutar si no hay datos ---
  useEffect(() => {
    if (!isDataReady || !selectedChart) return;

    const getData = (teamId, metric) =>
      calculateMetric(games, teamId, metric) || 0;

    const makeTeamData = (teamId) => ({
      rebounding: [
        calculateMetric(games, teamId, "ORB"),
        calculateMetric(games, teamId, "DRB"),
        calculateMetric(games, teamId, "REB"),
        calculateMetric(games, teamId, "OREB"),
        calculateMetric(games, teamId, "DREB"),
      ],
      shooting: [
        calculateMetric(games, teamId, "THREEPP"),
        calculateMetric(games, teamId, "TWOOPP"),
        calculateMetric(games, teamId, "FTP"),
        calculateMetric(games, teamId, "FG"),
        calculateMetric(games, teamId, "TSP"),
      ],
      assists: [
        calculateMetric(games, teamId, "ASS"),
        calculateMetric(games, teamId, "STL"),
        calculateMetric(games, teamId, "TO"),
      ],
      extras: [
        calculateMetric(games, teamId, "FL"),
        calculateMetric(games, teamId, "FLR"),
        calculateMetric(games, teamId, "BLK"),
        calculateMetric(games, teamId, "BLKR"),
      ],
    });

    const teamData = makeTeamData(activeTeam);
    const rivalData = makeTeamData(activeRival);

    let activeLabel = [];
    let activeData = [];
    let activeDataRival = [];

    switch (selectedChart) {
      case "Porcentaje de rebotes":
        activeLabel = reboundingLabels;
        activeData = teamData.rebounding;
        activeDataRival = rivalData.rebounding;
        break;
      case "Porcentaje de tiros":
        activeLabel = shootingLabels;
        activeData = teamData.shooting;
        activeDataRival = rivalData.shooting;
        break;
      case "Porcentaje de asistencias":
        activeLabel = assitsLabels;
        activeData = teamData.assists;
        activeDataRival = rivalData.assists;
        break;
      case "Porcentaje de tapas y faltas":
        activeLabel = extraLabels;
        activeData = teamData.extras;
        activeDataRival = rivalData.extras;
        break;
      default:
        break;
    }

    const barChartData = {
      labels: activeLabel,
      datasets: [
        {
          label: teams.find((t) => t._id === activeTeam)?.name || "Equipo base",
          data: activeData,
          backgroundColor: "rgba(255, 99, 132, 0.2)",
          borderColor: "rgba(255, 99, 132, 0.6)",
          borderWidth: 1,
        },
        {
          label:
            activeRival && teams.find((t) => t._id === activeRival)?.name
              ? teams.find((t) => t._id === activeRival)?.name
              : "Selecciona rival",
          data: activeDataRival,
          backgroundColor: "rgba(0, 0, 0, 0.2)",
          borderColor: "rgba(0, 0, 0, 0.6)",
          borderWidth: 1,
        },
      ],
    };

    // Validar datasets antes de actualizar Chart.js
    if (
      barChartData.datasets.some(
        (d) => !Array.isArray(d.data) || d.data.length === 0
      )
    ) {
      setChartData(null);
    } else {
      setChartData(barChartData);
    }

    setLoading(false);
  }, [selectedChart, games, baseTeam, activeRival, activeTeam]);

  // Opciones del gráfico
  const optionsBarChart = {
    responsive: true,
    maintainAspectRatio: false,
    scales: {
      y: {
        beginAtZero: true,
        ticks: { precision: 0 },
      },
    },
    plugins: {
      legend: { display: true },
      tooltip: { enabled: true },
    },
  };

  if (!isDataReady) {
    return <div>Cargando datos del equipo...</div>;
  }

  return (
    <div className="container-flex boxshadow shooting-radar-chart-box">
      <select
        id="league-select"
        onChange={(e) => setSelectedChart(e.target.value)}
        value={selectedChart || ""}
      >
        <option value="" hidden>
          Selecciona comparativa
        </option>
        <option>Porcentaje de tiros</option>
        <option>Porcentaje de rebotes</option>
        <option>Porcentaje de asistencias</option>
        <option>Porcentaje de tapas y faltas</option>
      </select>

      {loading ? (
        <p>Cargando gráfico...</p>
      ) : chartData ? (
        <Bar data={chartData} options={optionsBarChart} />
      ) : (
        <p>No hay datos suficientes para mostrar el gráfico.</p>
      )}
    </div>
  );
};

const StatisticsTable = ({
  baseTeam,
  activeRival,
  activeTeam,
  games,
  isH2HActive,
  activeSeason,
  activePhase,
  activeLeague,
  teams,
}) => {
  let rival = baseTeam === activeRival ? activeTeam : activeRival;
  let team = baseTeam === activeTeam ? activeTeam : activeRival;
  const filteredGames = games?.filter((game) => {
    const isH2HGame =
      (game.local_team === baseTeam && game.visit_team === rival) ||
      (game.local_team === rival && game.visit_team === baseTeam);

    return (
      (isH2HActive ? isH2HGame : games) &&
      game.season === activeSeason &&
      game.stage === activePhase &&
      game.category === activeLeague
    );
  });
  return (
    <table>
      <thead>
        <tr>
          <th scope="col"></th>
          <th scope="col">
            {
              teams?.filter((object) => {
                return object._id === team;
              })[0]?.name
            }
          </th>
          <th scope="col">
            {
              teams?.filter((object) => {
                return object._id === rival;
              })[0]?.name
            }
          </th>
        </tr>
        <tr>
          <th scope="col">Metrica</th>
          <th scope="col">Temporada</th>
          <th scope="col">Temporada</th>
        </tr>
      </thead>
      <tbody>
        <tr>
          <td>PJ</td>
          <td>
            {!isH2HActive &&
              games?.filter(
                (game) =>
                  game.local_team === baseTeam || game.visit_team === baseTeam
              ).length}
            {isH2HActive &&
              games.filter(
                (game) =>
                  (game.local_team === baseTeam && game.visit_team === rival) ||
                  (game.local_team === rival && game.visit_team === baseTeam)
              ).length}
          </td>
          {rival !== null ? (
            <td>
              {!isH2HActive &&
                games?.filter(
                  (game) =>
                    game.local_team === rival || game.visit_team === rival
                ).length}
              {isH2HActive &&
                games.filter(
                  (game) =>
                    (game.local_team === baseTeam &&
                      game.visit_team === rival) ||
                    (game.local_team === rival && game.visit_team === baseTeam)
                ).length}
            </td>
          ) : null}
        </tr>
        <tr>
          <td>PTS</td>
          <td>{calculateMetric(filteredGames, baseTeam, "PTS")}</td>
          {activeRival !== null ? (
            <td>{calculateMetric(filteredGames, rival, "PTS")}</td>
          ) : (
            0
          )}
        </tr>
        <tr>
          <td>FGM</td>
          <td>{calculateMetric(filteredGames, baseTeam, "FG")}</td>
          {rival !== null ? (
            <td>{calculateMetric(filteredGames, rival, "FG")}</td>
          ) : (
            0
          )}
        </tr>
        <tr>
          <td>FGA</td>
          <td>{calculateMetric(filteredGames, baseTeam, "FGA")}</td>
          {rival !== null ? (
            <td>{calculateMetric(filteredGames, rival, "FGA")}</td>
          ) : (
            0
          )}
        </tr>
        <tr>
          <td>FG%</td>
          <td>{calculateMetric(filteredGames, baseTeam, "FGP")}</td>
          {rival !== null ? (
            <td>{calculateMetric(filteredGames, rival, "FGP")}</td>
          ) : (
            0
          )}
        </tr>
        <tr>
          <td>3PM</td>
          <td>{calculateMetric(filteredGames, baseTeam, "THREEP")}</td>
          {rival !== null ? (
            <td>{calculateMetric(filteredGames, rival, "THREEP")}</td>
          ) : (
            0
          )}
        </tr>
        <tr>
          <td>3PA</td>
          <td>{calculateMetric(filteredGames, baseTeam, "THREEPA")}</td>
          {rival !== null ? (
            <td>{calculateMetric(filteredGames, rival, "THREEPA")}</td>
          ) : (
            0
          )}
        </tr>
        <tr>
          <td>3P%</td>
          <td>{calculateMetric(filteredGames, baseTeam, "THREEPP")}</td>
          {rival !== null ? (
            <td>{calculateMetric(filteredGames, rival, "THREEPP")}</td>
          ) : (
            0
          )}
        </tr>
        <tr>
          <td>2PM</td>
          <td>{calculateMetric(filteredGames, baseTeam, "TWOOP")}</td>
          {rival !== null ? (
            <td>{calculateMetric(filteredGames, rival, "TWOOP")}</td>
          ) : (
            0
          )}
        </tr>
        <tr>
          <td>2PA</td>
          <td>{calculateMetric(filteredGames, baseTeam, "TWOOPA")}</td>
          {rival !== null ? (
            <td>{calculateMetric(filteredGames, rival, "TWOOPA")}</td>
          ) : (
            0
          )}
        </tr>
        <tr>
          <td>2P%</td>
          <td>{calculateMetric(filteredGames, baseTeam, "TWOOPP")}</td>
          {rival !== null ? (
            <td>{calculateMetric(filteredGames, rival, "TWOOPP")}</td>
          ) : (
            0
          )}
        </tr>
        <tr>
          <td>FTM</td>
          <td>{calculateMetric(filteredGames, baseTeam, "FT")}</td>
          {activeRival !== null ? (
            <td>{calculateMetric(filteredGames, activeRival, "FT")}</td>
          ) : (
            0
          )}
        </tr>
        <tr>
          <td>FTA</td>
          <td>{calculateMetric(filteredGames, baseTeam, "FTA")}</td>
          {rival !== null ? (
            <td>{calculateMetric(filteredGames, rival, "FTA")}</td>
          ) : (
            0
          )}
        </tr>
        <tr>
          <td>FT%</td>
          <td>{calculateMetric(filteredGames, baseTeam, "FTP")}</td>
          {rival !== null ? (
            <td>{calculateMetric(filteredGames, rival, "FTP")}</td>
          ) : (
            0
          )}
        </tr>
        <tr>
          <td>OR</td>
          <td>{calculateMetric(filteredGames, baseTeam, "ORB")}</td>
          {rival !== null ? (
            <td>{calculateMetric(filteredGames, rival, "ORB")}</td>
          ) : (
            0
          )}
        </tr>
        <tr>
          <td>DR</td>
          <td>{calculateMetric(filteredGames, baseTeam, "DRB")}</td>
          {activeRival !== null ? (
            <td>{calculateMetric(filteredGames, activeRival, "DRB")}</td>
          ) : (
            0
          )}
        </tr>
        <tr>
          <td>REB</td>
          <td>{calculateMetric(filteredGames, baseTeam, "REB")}</td>
          {rival !== null ? (
            <td>{calculateMetric(filteredGames, rival, "REB")}</td>
          ) : (
            0
          )}
        </tr>
        <tr>
          <td>ASS</td>
          <td>{calculateMetric(filteredGames, baseTeam, "ASS")}</td>
          {rival !== null ? (
            <td>{calculateMetric(filteredGames, rival, "ASS")}</td>
          ) : (
            0
          )}
        </tr>
        <tr>
          <td>STL</td>
          <td>{calculateMetric(filteredGames, baseTeam, "STL")}</td>
          {rival !== null ? (
            <td>{calculateMetric(filteredGames, rival, "STL")}</td>
          ) : (
            0
          )}
        </tr>
        <tr>
          <td>BLK</td>
          <td>{calculateMetric(filteredGames, baseTeam, "BLK")}</td>
          {rival !== null ? (
            <td>{calculateMetric(filteredGames, rival, "BLK")}</td>
          ) : (
            0
          )}
        </tr>
        <tr>
          <td>PER</td>
          <td>{calculateMetric(filteredGames, baseTeam, "TORP")}</td>
          {rival !== null ? (
            <td>{calculateMetric(filteredGames, rival, "TORP")}</td>
          ) : (
            0
          )}
        </tr>
        <tr>
          <td>PF</td>
          <td>{calculateMetric(filteredGames, baseTeam, "FL")}</td>
          {rival !== null ? (
            <td>{calculateMetric(filteredGames, rival, "FL")}</td>
          ) : (
            0
          )}
        </tr>
      </tbody>
    </table>
  );
};

const TeamFourFactorsComparison = ({
  games,
  activeRival,
  activeTeam,
  teams,
  activeLeague,
}) => {
  const [data, setData] = useState([]);

  useEffect(() => {
    const metrics = ["eFG", "FTR", "OREB", "TOR"];

    // Crea un objeto con métricas y rankings
    const rankings = {};
    metrics.forEach((metric) => {
      const sorted = teams
        ?.filter((team) => team.league === activeLeague)
        .map((team) => ({
          _id: team._id,
          value: calculatePercj(games, team._id, metric),
        }))
        .sort((a, b) => {
          // En FTR, eFG y OREB, más alto es mejor
          // En TOR (turnover rate), más bajo es mejor
          if (metric === "TOR") return a.value - b.value;
          return b.value - a.value;
        });

      rankings[metric] = {};
      sorted?.forEach((team, idx) => {
        rankings[metric][team._id] = idx + 1;
      });
    });

    // Guarda los rankings para usar después
    setData(rankings);
  }, [activeLeague, teams]);

  const getRankColor = (rank, total) => {
    if (rank <= total * 0.25) return "#60C060"; // verde
    if (rank >= total * 0.75) return "#E56B6F"; // rojo
    return "#F7B500"; // amarillo
  };

  return (
    <table style={{ borderSpacing: 0, width: "100%", height: "100%" }}>
      <thead style={{ height: "33%" }}>
        <tr
          className={"header"}
          style={{ background: "#A5A5A5", fontWeight: "700", color: "#FFF" }}
        >
          <th
            contentEditable
            suppressContentEditableWarning
            style={{
              height: `${16}%`,
              border: "1px solid rgb(151,152,154)",
              gap: 0,
            }}
          >
            Equipo
          </th>
          <th
            style={{
              height: `${16}%`,
              border: "1px solid rgb(151,152,154)",
              gap: 0,
              fontWeight: 700,
            }}
          >
            eFG%
          </th>
          <th
            style={{
              height: `${16}%`,
              border: "1px solid rgb(151,152,154)",
              gap: 0,
            }}
          >
            TL%
          </th>
          <th
            style={{
              height: `${16}%`,
              border: "1px solid rgb(151,152,154)",
              gap: 0,
            }}
          >
            OREB%
          </th>
          <th
            style={{
              height: `${16}%`,
              border: "1px solid rgb(151,152,154)",
              gap: 0,
            }}
          >
            TOR%
          </th>
        </tr>
      </thead>
      <tbody>
        <tr style={{ height: "33%" }}>
          <td
            style={{
              border: "1px solid rgb(151,152,154)",
              gap: 0,
            }}
          >
            {
              teams?.filter((object) => {
                return object._id === activeTeam;
              })[0]?.name
            }
          </td>
          <td style={{ border: "1px solid rgb(151,152,154)", gap: 0 }}>
            {calculateMetric(games, activeTeam, "eFG")}%{" "}
            <span
              style={{
                background: getRankColor(
                  data?.eFG?.[activeTeam],
                  teams?.length
                ),
                padding: "2px 4px",
                borderRadius: "4px",
                fontSize: "0.85em",
                marginLeft: "6px",
              }}
            >
              ({data?.eFG?.[activeTeam]}°)
            </span>
          </td>
          <td style={{ border: "1px solid rgb(151,152,154)", gap: 0 }}>
            {calculateMetric(games, activeTeam, "FTR")}%{" "}
            <span
              style={{
                background: getRankColor(
                  data?.FTR?.[activeTeam],
                  teams?.length
                ),
                padding: "2px 4px",
                borderRadius: "4px",
                fontSize: "0.85em",
                marginLeft: "6px",
              }}
            >
              ({data?.FTR?.[activeTeam]}°)
            </span>
          </td>
          <td style={{ border: "1px solid rgb(151,152,154)", gap: 0 }}>
            {calculateMetric(games, activeTeam, "OREB")}%{" "}
            <span
              style={{
                background: getRankColor(
                  data?.OREB?.[activeTeam],
                  teams?.length
                ),
                padding: "2px 4px",
                borderRadius: "4px",
                fontSize: "0.85em",
                marginLeft: "6px",
              }}
            >
              ({data?.OREB?.[activeTeam]}°)
            </span>
          </td>
          <td style={{ border: "1px solid rgb(151,152,154)", gap: 0 }}>
            {calculateMetric(games, activeTeam, "TOR")}%{" "}
            <span
              style={{
                background: getRankColor(
                  data?.TOR?.[activeTeam],
                  teams?.length
                ),
                padding: "2px 4px",
                borderRadius: "4px",
                fontSize: "0.85em",
                marginLeft: "6px",
              }}
            >
              ({data?.TOR?.[activeTeam]}°)
            </span>
          </td>
        </tr>
        <tr style={{ height: "33%" }}>
          <td
            style={{
              border: "1px solid rgb(151,152,154)",
              gap: 0,
            }}
          >
            {
              teams?.filter((object) => {
                return object._id === activeRival;
              })[0]?.name
            }
          </td>
          <td style={{ border: "1px solid rgb(151,152,154)", gap: 0 }}>
            {calculateMetric(games, activeRival, "eFG")}%{" "}
            <span
              style={{
                background: getRankColor(
                  data?.eFG?.[activeRival],
                  teams?.length
                ),
                padding: "2px 4px",
                borderRadius: "4px",
                fontSize: "0.85em",
                marginLeft: "6px",
              }}
            >
              ({data?.eFG?.[activeRival]}°)
            </span>
          </td>
          <td style={{ border: "1px solid rgb(151,152,154)", gap: 0 }}>
            {calculateMetric(games, activeRival, "FTR")}%{" "}
            <span
              style={{
                background: getRankColor(
                  data?.FTR?.[activeRival],
                  teams?.length
                ),
                padding: "2px 4px",
                borderRadius: "4px",
                fontSize: "0.85em",
                marginLeft: "6px",
              }}
            >
              ({data?.FTR?.[activeRival]}°)
            </span>
          </td>
          <td style={{ border: "1px solid rgb(151,152,154)", gap: 0 }}>
            {calculateMetric(games, activeRival, "OREB")}%{" "}
            <span
              style={{
                background: getRankColor(
                  data?.OREB?.[activeRival],
                  teams?.length
                ),
                padding: "2px 4px",
                borderRadius: "4px",
                fontSize: "0.85em",
                marginLeft: "6px",
              }}
            >
              ({data?.OREB?.[activeRival]}°)
            </span>
          </td>
          <td style={{ border: "1px solid rgb(151,152,154)", gap: 0 }}>
            {calculateMetric(games, activeRival, "TOR")}%{" "}
            <span
              style={{
                background: getRankColor(
                  data?.TOR?.[activeRival],
                  teams?.length
                ),
                padding: "2px 4px",
                borderRadius: "4px",
                fontSize: "0.85em",
                marginLeft: "6px",
              }}
            >
              ({data?.TOR?.[activeRival]}°)
            </span>
          </td>
        </tr>
      </tbody>
    </table>
  );
};

const TeamLineupNaturalStats = ({
  activeSeason,
  activePhase,
  baseTeam,
  data,
  games,
  players,
}) => {
  return (
    <table style={{ borderSpacing: 0, width: "100%", height: "100%" }}>
      <thead style={{ height: "5%" }}>
        <tr
          className={"header"}
          style={{ background: "#A5A5A5", fontWeight: "700", color: "#FFF" }}
        >
          <th
            contentEditable
            suppressContentEditableWarning
            style={{
              height: `${16}%`,
              border: "1px solid rgb(151,152,154)",
              gap: 0,
            }}
          >
            Jugador
          </th>
          <th
            contentEditable
            suppressContentEditableWarning
            style={{
              height: `${16}%`,
              border: "1px solid rgb(151,152,154)",
              gap: 0,
            }}
          >
            PJ
          </th>
          <th
            contentEditable
            suppressContentEditableWarning
            style={{
              height: `${16}%`,
              border: "1px solid rgb(151,152,154)",
              gap: 0,
            }}
          >
            MINS
          </th>
          <th
            contentEditable
            suppressContentEditableWarning
            style={{
              height: `${16}%`,
              border: "1px solid rgb(151,152,154)",
              gap: 0,
            }}
          >
            PTS
          </th>
          <th
            contentEditable
            suppressContentEditableWarning
            style={{
              height: `${16}%`,
              border: "1px solid rgb(151,152,154)",
              gap: 0,
            }}
          >
            TL%
          </th>
          <th
            contentEditable
            suppressContentEditableWarning
            style={{
              height: `${16}%`,
              border: "1px solid rgb(151,152,154)",
              gap: 0,
            }}
          >
            2P%
          </th>
          <th
            contentEditable
            suppressContentEditableWarning
            style={{
              height: `${16}%`,
              border: "1px solid rgb(151,152,154)",
              gap: 0,
            }}
          >
            3P%
          </th>
          <th
            contentEditable
            suppressContentEditableWarning
            style={{
              height: `${16}%`,
              border: "1px solid rgb(151,152,154)",
              gap: 0,
            }}
          >
            REBO
          </th>
          <th
            contentEditable
            suppressContentEditableWarning
            style={{
              height: `${16}%`,
              border: "1px solid rgb(151,152,154)",
              gap: 0,
            }}
          >
            REBD
          </th>
          <th
            contentEditable
            suppressContentEditableWarning
            style={{
              height: `${16}%`,
              border: "1px solid rgb(151,152,154)",
              gap: 0,
            }}
          >
            REB
          </th>
          <th
            contentEditable
            suppressContentEditableWarning
            style={{
              height: `${16}%`,
              border: "1px solid rgb(151,152,154)",
              gap: 0,
            }}
          >
            AS
          </th>
          <th
            contentEditable
            suppressContentEditableWarning
            style={{
              height: `${16}%`,
              border: "1px solid rgb(151,152,154)",
              gap: 0,
            }}
          >
            REC
          </th>
          <th
            contentEditable
            suppressContentEditableWarning
            style={{
              height: `${16}%`,
              border: "1px solid rgb(151,152,154)",
              gap: 0,
            }}
          >
            PER
          </th>
        </tr>
      </thead>
      {games === null && games !== undefined ? (
        <tbody>
          {[...Array(12)].map((_, i) => (
            <tr key={i}>
              <td>Nombre</td>
              <td className="value">0</td>
              <td className="value">0</td>
              <td className="value">0</td>
              <td className="value">0</td>
              <td className="value">0</td>
              <td className="value">0</td>
              <td className="value">0</td>
              <td className="value">0</td>
              <td className="value">0</td>
              <td className="value">0</td>
              <td className="value">0</td>
              <td className="value">0</td>
            </tr>
          ))}
        </tbody>
      ) : (
        <tbody>
          {players
            ?.sort((a, b) => {
              const nameA = a.player_name.toUpperCase();
              const nameB = b.player_name.toUpperCase();
              if (nameA < nameB) return -1;
              if (nameA > nameB) return 1;
              return 0;
            })
            ?.map((player) => ({
              ...player,
              statValue: calculatePlayerStats(
                games.filter(
                  (game) =>
                    (game.local_team === baseTeam ||
                      game.visit_team === baseTeam) &&
                    game.season === activeSeason &&
                    (activePhase === null || game.stage === activePhase)
                ),
                player._id,
                "PTS"
              ),
            }))
            .map((player, index) => (
              <tr key={index} style={{ height: "20px" }}>
                <td
                  style={{
                    border: "1px solid rgb(151,152,154)",
                    gap: 0,
                    width: "30%",
                    padding: "0.3rem",
                    textAlign: "left",
                  }}
                >
                  {player.player_name.toUpperCase()}{" "}
                  {player.player_lastname.toUpperCase()}
                </td>
                <td
                  style={{
                    border: "1px solid rgb(151,152,154)",
                    gap: 0,
                    width: "auto",
                    textAlign: "center",
                  }}
                >
                  {calculatePlayerStats(
                    games.filter(
                      (game) =>
                        (game.local_team === baseTeam ||
                          game.visit_team === baseTeam) &&
                        game.season === activeSeason &&
                        (activePhase === null || game.stage === activePhase)
                    ),
                    player._id,
                    "GAMES"
                  )}
                </td>
                <td
                  className="value"
                  style={{
                    border: "1px solid rgb(151,152,154)",
                    gap: 0,
                    width: "auto",
                    textAlign: "center",
                  }}
                >
                  {calculatePlayerStats(
                    games.filter(
                      (game) =>
                        (game.local_team === baseTeam ||
                          game.visit_team === baseTeam) &&
                        game.season === activeSeason &&
                        (activePhase === null || game.stage === activePhase)
                    ),
                    player._id,
                    "MINSP"
                  )}
                </td>
                <td
                  className="value"
                  style={{
                    border: "1px solid rgb(151,152,154)",
                    gap: 0,
                    width: "auto",
                    textAlign: "center",
                  }}
                >
                  {calculatePlayerStats(
                    games.filter(
                      (game) =>
                        (game.local_team === baseTeam ||
                          game.visit_team === baseTeam) &&
                        game.season === activeSeason &&
                        (activePhase === null || game.stage === activePhase)
                    ),
                    player._id,
                    "PTS"
                  )}
                </td>
                <td
                  className="value"
                  style={{
                    border: "1px solid rgb(151,152,154)",
                    gap: 0,
                    width: "auto",
                    textAlign: "center",
                  }}
                >
                  {calculatePlayerStats(
                    games.filter(
                      (game) =>
                        (game.local_team === baseTeam ||
                          game.visit_team === baseTeam) &&
                        game.season === activeSeason &&
                        (activePhase === null || game.stage === activePhase)
                    ),
                    player._id,
                    "FTP"
                  )}
                </td>
                <td
                  className="value"
                  style={{
                    border: "1px solid rgb(151,152,154)",
                    gap: 0,
                    width: "auto",
                    textAlign: "center",
                  }}
                >
                  {calculatePlayerStats(
                    games.filter(
                      (game) =>
                        (game.local_team === baseTeam ||
                          game.visit_team === baseTeam) &&
                        game.season === activeSeason &&
                        (activePhase === null || game.stage === activePhase)
                    ),
                    player._id,
                    "TWOOPP"
                  )}
                </td>
                <td
                  className="value"
                  style={{
                    border: "1px solid rgb(151,152,154)",
                    gap: 0,
                    width: "auto",
                    textAlign: "center",
                  }}
                >
                  {calculatePlayerStats(
                    games.filter(
                      (game) =>
                        (game.local_team === baseTeam ||
                          game.visit_team === baseTeam) &&
                        game.season === activeSeason &&
                        (activePhase === null || game.stage === activePhase)
                    ),
                    player._id,
                    "THREEPP"
                  )}
                </td>
                <td
                  className="value"
                  style={{
                    border: "1px solid rgb(151,152,154)",
                    gap: 0,
                    width: "auto",
                    textAlign: "center",
                  }}
                >
                  {calculatePlayerStats(
                    games.filter(
                      (game) =>
                        (game.local_team === baseTeam ||
                          game.visit_team === baseTeam) &&
                        game.season === activeSeason &&
                        (activePhase === null || game.stage === activePhase)
                    ),
                    player._id,
                    "ORB"
                  )}
                </td>
                <td
                  className="value"
                  style={{
                    border: "1px solid rgb(151,152,154)",
                    gap: 0,
                    width: "auto",
                    textAlign: "center",
                  }}
                >
                  {calculatePlayerStats(
                    games.filter(
                      (game) =>
                        (game.local_team === baseTeam ||
                          game.visit_team === baseTeam) &&
                        game.season === activeSeason &&
                        (activePhase === null || game.stage === activePhase)
                    ),
                    player._id,
                    "DRB"
                  )}
                </td>
                <td
                  className="value"
                  style={{
                    border: "1px solid rgb(151,152,154)",
                    gap: 0,
                    width: "auto",
                    textAlign: "center",
                  }}
                >
                  {calculatePlayerStats(
                    games.filter(
                      (game) =>
                        (game.local_team === baseTeam ||
                          game.visit_team === baseTeam) &&
                        game.season === activeSeason &&
                        (activePhase === null || game.stage === activePhase)
                    ),
                    player._id,
                    "REB"
                  )}
                </td>
                <td
                  className="value"
                  style={{
                    border: "1px solid rgb(151,152,154)",
                    gap: 0,
                    width: "auto",
                    textAlign: "center",
                  }}
                >
                  {calculatePlayerStats(
                    games.filter(
                      (game) =>
                        (game.local_team === baseTeam ||
                          game.visit_team === baseTeam) &&
                        game.season === activeSeason &&
                        (activePhase === null || game.stage === activePhase)
                    ),
                    player._id,
                    "ASS"
                  )}
                </td>
                <td
                  className="value"
                  style={{
                    border: "1px solid rgb(151,152,154)",
                    gap: 0,
                    width: "auto",
                    textAlign: "center",
                  }}
                >
                  {calculatePlayerStats(
                    games.filter(
                      (game) =>
                        (game.local_team === baseTeam ||
                          game.visit_team === baseTeam) &&
                        game.season === activeSeason &&
                        (activePhase === null || game.stage === activePhase)
                    ),
                    player._id,
                    "STL"
                  )}
                </td>
                <td
                  className="value"
                  style={{
                    border: "1px solid rgb(151,152,154)",
                    gap: 0,
                    width: "auto",
                    textAlign: "center",
                  }}
                >
                  {calculatePlayerStats(
                    games.filter(
                      (game) =>
                        (game.local_team === baseTeam ||
                          game.visit_team === baseTeam) &&
                        game.season === activeSeason &&
                        (activePhase === null || game.stage === activePhase)
                    ),
                    player._id,
                    "TO"
                  )}
                </td>
              </tr>
            ))}
        </tbody>
      )}
    </table>
  );
};

const TeamLineupFourFactoesStats = ({
  activeSeason,
  activePhase,
  baseTeam,
  data,
  games,
  players,
}) => {
  return (
    <div>
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          paddingBottom: "2rem",
        }}
      >
        <h3
          style={{
            letterSpacing: "-0.025em",
            fontSize: "1.5rem",
            fontWeight: "700",
            lineHeight: "2rem",
            textAlign: "center",
          }}
        >
          Estadísticas del Equipo - Four Factors
        </h3>
        <div
          style={{
            display: "flex",
            flexDirection: "row",
            fontSize: "0.875rem",
            lineHeight: "1.25rem",
            justifyContent: "center",
          }}
        >
          <div
            style={{
              alignItems: "center",
              display: "flex",
            }}
          >
            <span>
              <b>eFG%:</b> Porcentaje de Tiro Efectivo
            </span>
          </div>
          <div
            style={{
              alignItems: "center",
              display: "flex",
              marginLeft: "1rem",
            }}
          >
            <span>
              <b>TL%:</b> Porcentaje de Tiros Libres
            </span>
          </div>
          <div
            style={{
              alignItems: "center",
              display: "flex",
              marginLeft: "1rem",
            }}
          >
            <span>
              <b>OREB%:</b> Rebotes Ofensivos
            </span>
          </div>
          <div
            style={{
              alignItems: "center",
              display: "flex",
              marginLeft: "1rem",
            }}
          >
            <span>
              <b>TOR%:</b> Pérdidas de Balón
            </span>
          </div>
        </div>
      </div>
      <table style={{ borderSpacing: 0, width: "100%", height: "100%" }}>
        <thead style={{ height: "15%" }}>
          <tr
            className={"header"}
            style={{ background: "#A5A5A5", fontWeight: "700", color: "#FFF" }}
          >
            <th
              contentEditable
              suppressContentEditableWarning
              style={{
                height: `${16}%`,
                border: "1px solid rgb(151,152,154)",
                gap: 0,
              }}
            >
              Jugador
            </th>
            <th
              contentEditable
              suppressContentEditableWarning
              style={{
                height: `${16}%`,
                border: "1px solid rgb(151,152,154)",
                gap: 0,
              }}
            >
              eFG%
            </th>
            <th
              contentEditable
              suppressContentEditableWarning
              style={{
                height: `${16}%`,
                border: "1px solid rgb(151,152,154)",
                gap: 0,
              }}
            >
              TL%
            </th>
            <th
              contentEditable
              suppressContentEditableWarning
              style={{
                height: `${16}%`,
                border: "1px solid rgb(151,152,154)",
                gap: 0,
              }}
            >
              OREB%
            </th>
            <th
              contentEditable
              suppressContentEditableWarning
              style={{
                height: `${16}%`,
                border: "1px solid rgb(151,152,154)",
                gap: 0,
              }}
            >
              TOR%
            </th>
          </tr>
        </thead>
        {games === null ? (
          <tbody>
            {[...Array(15)].map((_, i) => (
              <tr key={i} style={{ height: "50px" }}>
                <td>Nombre</td>
                <td
                  className="value"
                  style={{
                    border: "1px solid rgb(151,152,154)",
                    gap: 0,
                    width: "auto",
                    textAlign: "center",
                  }}
                >
                  0
                </td>
                <td
                  className="value"
                  style={{
                    border: "1px solid rgb(151,152,154)",
                    gap: 0,
                    width: "auto",
                    textAlign: "center",
                  }}
                >
                  0
                </td>
                <td
                  className="value"
                  style={{
                    border: "1px solid rgb(151,152,154)",
                    gap: 0,
                    width: "auto",
                    textAlign: "center",
                  }}
                >
                  0
                </td>
                <td
                  className="value"
                  style={{
                    border: "1px solid rgb(151,152,154)",
                    gap: 0,
                    width: "auto",
                    textAlign: "center",
                  }}
                >
                  0
                </td>
              </tr>
            ))}
          </tbody>
        ) : (
          <tbody>
            {players
              ?.sort((a, b) => {
                const nameA = a.player_name.toUpperCase();
                const nameB = b.player_name.toUpperCase();
                if (nameA < nameB) return -1;
                if (nameA > nameB) return 1;
                return 0;
              })
              ?.map((player) => ({
                ...player,
                statValue: calculatePlayerStats(
                  games.filter(
                    (game) =>
                      (game.local_team === baseTeam ||
                        game.visit_team === baseTeam) &&
                      game.season === activeSeason &&
                      (activePhase === null || game.stage === activePhase)
                  ),
                  player._id,
                  "PTS"
                ),
              }))
              .map((player, index) => (
                <tr key={index} style={{ height: "50px" }}>
                  <td
                    style={{
                      border: "1px solid rgb(151,152,154)",
                      gap: 0,
                      width: "30%",
                      padding: "0.3rem",
                      textAlign: "left",
                    }}
                  >
                    {player.player_name.toUpperCase()}{" "}
                    {player.player_lastname.toUpperCase()}
                  </td>
                  <td
                    style={{
                      border: "1px solid rgb(151,152,154)",
                      gap: 0,
                      width: "auto",
                      textAlign: "center",
                    }}
                  >
                    {calculatePlayerStats(
                      games.filter(
                        (game) =>
                          (game.local_team === baseTeam ||
                            game.visit_team === baseTeam) &&
                          game.season === activeSeason &&
                          (activePhase === null || game.stage === activePhase)
                      ),
                      player._id,
                      "eFG"
                    )}
                  </td>
                  <td
                    style={{
                      border: "1px solid rgb(151,152,154)",
                      gap: 0,
                      width: "auto",
                      textAlign: "center",
                    }}
                  >
                    {calculatePlayerStats(
                      games.filter(
                        (game) =>
                          (game.local_team === baseTeam ||
                            game.visit_team === baseTeam) &&
                          game.season === activeSeason &&
                          (activePhase === null || game.stage === activePhase)
                      ),
                      player._id,
                      "FTR"
                    )}
                  </td>
                  <td
                    style={{
                      border: "1px solid rgb(151,152,154)",
                      gap: 0,
                      width: "auto",
                      textAlign: "center",
                    }}
                  >
                    {calculatePlayerStats(
                      games.filter(
                        (game) =>
                          (game.local_team === baseTeam ||
                            game.visit_team === baseTeam) &&
                          game.season === activeSeason &&
                          (activePhase === null || game.stage === activePhase)
                      ),
                      player._id,
                      "OREB"
                    )}
                  </td>
                  <td
                    style={{
                      border: "1px solid rgb(151,152,154)",
                      gap: 0,
                      width: "auto",
                      textAlign: "center",
                    }}
                  >
                    {calculatePlayerStats(
                      games.filter(
                        (game) =>
                          (game.local_team === baseTeam ||
                            game.visit_team === baseTeam) &&
                          game.season === activeSeason &&
                          (activePhase === null || game.stage === activePhase)
                      ),
                      player._id,
                      "TOR"
                    )}
                  </td>
                </tr>
              ))}
          </tbody>
        )}
      </table>{" "}
    </div>
  );
};

const LineupsAdvanceStats = ({
  activeSeason,
  activePhase,
  baseTeam,
  data,
  games,
  players,
}) => {
  return (
    <table style={{ borderSpacing: 0, width: "100%", height: "100%" }}>
      <thead style={{ height: "15%" }}>
        <tr
          className={"header"}
          style={{ background: "#A5A5A5", fontWeight: "700", color: "#FFF" }}
        >
          <th
            contentEditable
            suppressContentEditableWarning
            style={{
              height: `${16}%`,
              border: "1px solid rgb(151,152,154)",
              gap: 0,
            }}
          >
            Jugador
          </th>
          <th
            contentEditable
            suppressContentEditableWarning
            style={{
              height: `${16}%`,
              border: "1px solid rgb(151,152,154)",
              gap: 0,
            }}
          >
            PJ
          </th>
          <th
            contentEditable
            suppressContentEditableWarning
            style={{
              height: `${16}%`,
              border: "1px solid rgb(151,152,154)",
              gap: 0,
            }}
          >
            MINS
          </th>
          <th
            contentEditable
            suppressContentEditableWarning
            style={{
              height: `${16}%`,
              border: "1px solid rgb(151,152,154)",
              gap: 0,
            }}
          >
            eFG%
          </th>
          <th
            contentEditable
            suppressContentEditableWarning
            style={{
              height: `${16}%`,
              border: "1px solid rgb(151,152,154)",
              gap: 0,
            }}
          >
            TL%
          </th>
          <th
            contentEditable
            suppressContentEditableWarning
            style={{
              height: `${16}%`,
              border: "1px solid rgb(151,152,154)",
              gap: 0,
            }}
          >
            TS%
          </th>
          <th
            contentEditable
            suppressContentEditableWarning
            style={{
              height: `${16}%`,
              border: "1px solid rgb(151,152,154)",
              gap: 0,
            }}
          >
            USO%
          </th>
        </tr>
      </thead>
      {games === null ? (
        <tbody>
          {[...Array(15)].map((_, i) => (
            <tr key={i} style={{ height: "50px" }}>
              <td>Nombre</td>
              <td
                className="value"
                style={{
                  border: "1px solid rgb(151,152,154)",
                  gap: 0,
                  width: "auto",
                  textAlign: "center",
                }}
              >
                0
              </td>
              <td
                className="value"
                style={{
                  border: "1px solid rgb(151,152,154)",
                  gap: 0,
                  width: "auto",
                  textAlign: "center",
                }}
              >
                0
              </td>
              <td
                className="value"
                style={{
                  border: "1px solid rgb(151,152,154)",
                  gap: 0,
                  width: "auto",
                  textAlign: "center",
                }}
              >
                0
              </td>
              <td
                className="value"
                style={{
                  border: "1px solid rgb(151,152,154)",
                  gap: 0,
                  width: "auto",
                  textAlign: "center",
                }}
              >
                0
              </td>
            </tr>
          ))}
        </tbody>
      ) : (
        <tbody>
          {players
            ?.sort((a, b) => {
              const nameA = a.player_name.toUpperCase();
              const nameB = b.player_name.toUpperCase();
              if (nameA < nameB) return -1;
              if (nameA > nameB) return 1;
              return 0;
            })
            ?.map((player) => ({
              ...player,
              statValue: calculatePlayerStats(
                games.filter(
                  (game) =>
                    (game.local_team === baseTeam ||
                      game.visit_team === baseTeam) &&
                    game.season === activeSeason &&
                    (activePhase === null || game.stage === activePhase)
                ),
                player._id,
                "PTS"
              ),
            }))
            .map((player, index) => (
              <tr key={index} style={{ height: "50px" }}>
                <td
                  style={{
                    border: "1px solid rgb(151,152,154)",
                    gap: 0,
                    width: "30%",
                    padding: "0.3rem",
                    textAlign: "left",
                  }}
                >
                  {player.player_name.toUpperCase()}{" "}
                  {player.player_lastname.toUpperCase()}
                </td>
                <td
                  style={{
                    border: "1px solid rgb(151,152,154)",
                    gap: 0,
                    width: "auto",
                    textAlign: "center",
                  }}
                >
                  {calculatePlayerStats(
                    games.filter(
                      (game) =>
                        (game.local_team === baseTeam ||
                          game.visit_team === baseTeam) &&
                        game.season === activeSeason &&
                        (activePhase === null || game.stage === activePhase)
                    ),
                    player._id,
                    "GAMES"
                  )}
                </td>
                <td
                  style={{
                    border: "1px solid rgb(151,152,154)",
                    gap: 0,
                    width: "auto",
                    textAlign: "center",
                  }}
                >
                  {calculatePlayerStats(
                    games.filter(
                      (game) =>
                        (game.local_team === baseTeam ||
                          game.visit_team === baseTeam) &&
                        game.season === activeSeason &&
                        (activePhase === null || game.stage === activePhase)
                    ),
                    player._id,
                    "MINSP"
                  )}
                </td>
                <td
                  style={{
                    border: "1px solid rgb(151,152,154)",
                    gap: 0,
                    width: "auto",
                    textAlign: "center",
                  }}
                >
                  {calculatePlayerStats(
                    games.filter(
                      (game) =>
                        (game.local_team === baseTeam ||
                          game.visit_team === baseTeam) &&
                        game.season === activeSeason &&
                        (activePhase === null || game.stage === activePhase)
                    ),
                    player._id,
                    "eFG"
                  )}
                </td>
                <td
                  style={{
                    border: "1px solid rgb(151,152,154)",
                    gap: 0,
                    width: "auto",
                    textAlign: "center",
                  }}
                >
                  {calculatePlayerStats(
                    games.filter(
                      (game) =>
                        (game.local_team === baseTeam ||
                          game.visit_team === baseTeam) &&
                        game.season === activeSeason &&
                        (activePhase === null || game.stage === activePhase)
                    ),
                    player._id,
                    "TSP"
                  )}
                </td>
                <td
                  style={{
                    border: "1px solid rgb(151,152,154)",
                    gap: 0,
                    width: "auto",
                    textAlign: "center",
                  }}
                >
                  {calculatePlayerStats(
                    games.filter(
                      (game) =>
                        (game.local_team === baseTeam ||
                          game.visit_team === baseTeam) &&
                        game.season === activeSeason &&
                        (activePhase === null || game.stage === activePhase)
                    ),
                    player._id,
                    "FTR"
                  )}
                </td>
                <td
                  style={{
                    border: "1px solid rgb(151,152,154)",
                    gap: 0,
                    width: "auto",
                    textAlign: "center",
                  }}
                >
                  {calculatePlayerStats(
                    games.filter(
                      (game) =>
                        (game.local_team === baseTeam ||
                          game.visit_team === baseTeam) &&
                        game.season === activeSeason &&
                        (activePhase === null || game.stage === activePhase)
                    ),
                    player._id,
                    "USG"
                  )}
                </td>
              </tr>
            ))}
        </tbody>
      )}
    </table>
  );
};

export const defaultItems = [
  {
    id: "statisticsTable",
    content: <StatisticsTable local_team="Team A" visit_team="Team B" />,
    isComponent: true,
    label: "Comparativas",
    logo: "Stats-table-icon.png",
    x: 0,
    y: 0,
    width: 270,
    height: 570,
  },
  {
    id: "Title",
    content: <TitleTool />,
    isComponent: true,
    label: "Título",
    logo: "title-icon.svg",
    x: 0,
    y: 0,
    width: 330,
    height: 90,
  },
  {
    id: "TeamPerformance",
    props: {},
    content: <TeamPerformance />,
    isComponent: true,
    label: "Rendimiento de equipo",
    logo: "team-performance-icon.png",
    x: 0,
    y: 0,
  },
  {
    id: "BasicTeamStats",
    content: <BasicTeamStats />,
    isComponent: true,
    label: "Estadísticas naturales",
    logo: "stats-icon.png",
    x: 0,
    y: 0,
    width: 500,
    height: 190,
  },
  {
    id: "AdvancedTeamStats",
    content: <AdvancedTeamStats />,
    isComponent: true,
    label: "Estadísticas avanzadas",
    logo: "advanced-stats-icon.png",
    x: 0,
    y: 0,
    width: 500,
    height: 236,
  },
  {
    id: "ComparisonBarChart",
    content: <ComparisonBarChart description="Porcentaje de rebotes" />,
    isComponent: true,
    label: "Gráfico de barras",
    logo: "comparison-icon.png",
    x: 0,
    y: 0,
    selectedOption: null,
  },
  {
    id: "Note",
    listType: null,
    content: <List listType={null} />,
    isComponent: true,
    label: "Nota",
    logo: "Note-icon.svg",
    x: 0,
    y: 0,
  },
  {
    id: "Table",
    rows: 4,
    columns: 3,
    content: <Table rows={4} cols={3} />,
    isComponent: true,
    label: "Tabla",
    logo: "table-icon.png",
    x: 0,
    y: 0,
    width: 270,
    height: 125,
  },
  {
    id: "Featured",
    listType: null,
    content: <FeaturedTable />,
    isComponent: true,
    label: "Desacados",
    logo: "Featured-icon.png",
    x: 0,
    y: 0,
  },
  {
    id: "Image",
    content: <ImageTool />,
    isComponent: true,
    label: "Imagen",
    logo: "image-icon.png",
    x: 0,
    y: 0,
    width: 270,
    height: 125,
    onImageLoad: (width, height) => ({ width, height }),
  },
  {
    id: "Link",
    content: <LinkTool />,
    isComponent: true,
    label: "Hipervinculo",
    logo: "link-icon.png",
    x: 0,
    y: 0,
    width: 270,
    height: 125,
  },
  {
    id: "ComparisonPlayerChart",
    content: <ComparisonPlayerChart />,
    isComponent: true,
    label: "Rendimiento jugador",
    logo: "ComparisonPlayerChart-icon.png",
    x: 0,
    y: 0,
    width: 270,
    height: 125,
  },
  {
    id: "PlayerResume",
    content: <PlayerResume />,
    isComponent: true,
    label: "Resumen jugador",
    logo: "player-resume-icon.png",
    x: 0,
    y: 0,
    width: 270,
    height: 125,
  },
  {
    id: "PlayerStatistics",
    content: <PlayerStatistics />,
    isComponent: true,
    label: "Estadísticas jugador",
    logo: "player-statistics-icon.png",
    x: 0,
    y: 0,
    width: 270,
    height: 750,
  },
  {
    id: "TeamFourFactorsComparison",
    content: <TeamFourFactorsComparison />,
    isComponent: true,
    label: "Comparativa 4 factores equipos",
    logo: "",
    x: 0,
    y: 0,
    width: 270,
    height: 125,
  },
  {
    id: "TeamLineupNaturalStats",
    content: <TeamLineupNaturalStats />,
    isComponent: true,
    label: "Estadisticas naturales alineaciones",
    logo: "team-stats-icon.svg",
    x: 0,
    y: 0,
    width: 270,
    height: 125,
  },
  {
    id: "TeamLineupFourFactoesStats",
    content: <TeamLineupFourFactoesStats />,
    isComponent: true,
    label: "4 factores alineaciones",
    logo: "",
    x: 0,
    y: 0,
    width: 270,
    height: 125,
  },
  {
    id: "LineupsAdvanceStats",
    content: <LineupsAdvanceStats />,
    isComponent: true,
    label: "Estadisticas avanzadas alineaciones",
    logo: "team-advanced-stats-icon.svg",
    x: 0,
    y: 0,
    width: 270,
    height: 125,
  },
];
